import React, { useState, useEffect } from "react";
import axios from "axios";
import moment from "moment";
import Chart from "react-apexcharts";
import { FiInfo } from "react-icons/fi";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { BASE_URL } from "../../../../../BaseUrl";
import { getCookie } from "../../../../../functions/Functions";
import "./FinanceDashboard.css";

const formatCurrency = (val) => {
  if (val === null || val === undefined) return "Rs 0";
  const num = Number(val);
  if (isNaN(num)) return String(val);
  const absNum = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  if (absNum >= 10000000) {
    return `${sign}Rs ${(absNum / 10000000).toFixed(2)} Cr`;
  }
  if (absNum >= 100000) {
    return `${sign}Rs ${(absNum / 100000).toFixed(2)} L`;
  }
  return `${sign}Rs ${num.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
};

const CashInflowOutflowWidget = ({ startDate, endDate, branch }) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    const fromDate = startDate
      ? moment(startDate).format("YYYY-MM-DD")
      : moment().subtract(30, "days").format("YYYY-MM-DD");
    const toDate = endDate
      ? moment(endDate).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");

    const payload = { fromDate, toDate };
    if (branch) {
      if (Array.isArray(branch) && branch.length > 0) {
        payload.branch_id = branch.map((b) => b.id);
      } else if (branch?.id) {
        payload.branch_id = branch.id;
      }
    }

    try {
      const response = await axios.post(
        `${BASE_URL}/widgets/accounting-monthly-cash-flow`,
        payload,
        {
          headers: { Authorization: `Bearer ${getCookie()}` },
        }
      );
      if (response?.data?.data) {
        setData(response.data.data);
      } else if (response?.data) {
        setData(response.data);
      }
    } catch (error) {
      console.error("Error fetching monthly cash flow:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, branch]);

  const monthlyList = data?.monthly_cash_flow || [];
  const categories = monthlyList.map(
    (m) => m.month_name || m.year_month || "Month"
  );
  const cashIn = monthlyList.map((m) => Number(m.cash_in) || 0);
  const cashOut = monthlyList.map((m) =>
    Number(m.cash_out) ? -Math.abs(Number(m.cash_out)) : 0
  );
  const netCashFlow = monthlyList.map((m) => Number(m.net_cash_flow) || 0);

  const series = [
    {
      name: "Inflow",
      type: "column",
      data: cashIn,
    },
    {
      name: "Outflow",
      type: "column",
      data: cashOut,
    },
    {
      name: "Closing balance",
      type: "line",
      data: netCashFlow,
    },
  ];

  const options = {
    chart: {
      type: "line",
      stacked: false,
      toolbar: { show: false },
      fontFamily: "inherit",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 600,
      },
    },
    stroke: {
      width: [0, 0, 3],
      curve: "smooth",
    },
    plotOptions: {
      bar: {
        columnWidth: "42%",
        borderRadius: 5,
      },
    },
    colors: ["#16a34a", "#dc2626", "#2563eb"],
    labels: categories,
    xaxis: {
      type: "category",
      categories: categories,
      labels: {
        style: {
          colors: "#6b7280",
          fontSize: "12px",
          fontWeight: 500,
        },
      },
    },
    yaxis: {
      labels: {
        formatter: (val) => {
          if (val === 0) return "Rs 0";
          const absVal = Math.abs(val);
          const sign = val < 0 ? "-" : "";
          if (absVal >= 1000000) return `${sign}Rs ${(absVal / 1000000).toFixed(1)}M`;
          if (absVal >= 1000) return `${sign}Rs ${(absVal / 1000).toFixed(0)}k`;
          return `${sign}Rs ${absVal}`;
        },
        style: {
          colors: "#6b7280",
          fontSize: "12px",
        },
      },
    },
    grid: {
      strokeDashArray: 4,
      borderColor: "#e5e7eb",
    },
    legend: {
      show: false,
    },
    states: {
      hover: {
        filter: { type: "darken", value: 0.92 },
      },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val) => {
          const absVal = Math.abs(val);
          const sign = val < 0 ? "-" : "";
          return `${sign}Rs ${absVal.toLocaleString()}`;
        },
      },
    },
  };

  const totalIn = data?.total_cash_in ?? 0;
  const totalOut = data?.total_cash_out ?? 0;
  const totalNet = data?.total_net_cash_flow ?? 0;

  return (
    <div className="cash-flow-card">
      {/* Header */}
      <div className="cash-flow-header">
        <div>
          <div className="d-flex align-items-center gap-1">
            <h5 className="cash-flow-title m-0 mr-1">Cash Inflow vs Outflow</h5>
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id="cash-inflow-outflow-info-tooltip">
                  Tracks monthly operational receipts (Inflow) against expenditures (Outflow) and net operating closing balance.
                </Tooltip>
              }
            >
              <span className="ms-1" style={{ cursor: "pointer" }}>
                <FiInfo className="text-muted" style={{ fontSize: "14px" }} />
              </span>
            </OverlayTrigger>
          </div>
          <span className="cash-flow-subtitle">With closing balance</span>
        </div>
      </div>

      {/* Summary Pills / Legend */}
      <div className="cash-flow-summary-pills">
        <div className="cash-flow-pill">
          <span className="cash-flow-dot dot-inflow" style={{ width: 10, height: 10, borderRadius: 2 }} />
          <span className="cash-flow-pill-label">Inflow:</span>
          <span className="cash-flow-pill-val">{formatCurrency(totalIn)}</span>
        </div>
        <div className="cash-flow-pill">
          <span className="cash-flow-dot dot-outflow" style={{ width: 10, height: 10, borderRadius: 2 }} />
          <span className="cash-flow-pill-label">Outflow:</span>
          <span className="cash-flow-pill-val">{formatCurrency(totalOut)}</span>
        </div>
        <div className="cash-flow-pill">
          <span className="cash-flow-dot dot-closing" style={{ width: 10, height: 10, borderRadius: 2 }} />
          <span className="cash-flow-pill-label">Closing balance:</span>
          <span className="cash-flow-pill-val">{formatCurrency(totalNet)}</span>
        </div>
      </div>

      {/* Chart Body */}
      {isLoading ? (
        <div className="cash-flow-chart-container py-3">
          <div
            className="cash-flow-skeleton-box w-100"
            style={{ height: "260px" }}
          />
        </div>
      ) : (
        <div className="cash-flow-chart-container">
          <Chart
            options={options}
            series={series}
            type="line"
            height={260}
            width="100%"
          />
        </div>
      )}
    </div>
  );
};

export default CashInflowOutflowWidget;
